import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * `V2_FRAME` — which application frame renders around the protected routes:
 * the legacy TailAdmin shell (`AppSidebar` + `AppHeader`) or `AppFrameV2`.
 *
 * V2 is now the default frame. Three ways to select the frame, most specific
 * first:
 *
 * 1. `?frame=v2` on any URL, which sticks for the session (`?frame=legacy`
 *    goes back). This is how the frame gets reviewed against real data
 *    without a deploy.
 * 2. `localStorage['av2.frame']` = `v2` | `legacy`, what (1) writes.
 * 3. `REACT_APP_V2_FRAME=false` at build time, as an emergency deployment
 *    rollback. Without an explicit override, the V2 frame renders.
 */
export const V2_FRAME_KEY = 'av2.frame';

/**
 * Pages that render their own `PageHeader` and fill `main`. Everything else
 * is still a legacy page and keeps the legacy content column and base type
 * (`v2-legacy-page` in tokens.css). Add a prefix as each page migrates; when
 * the list covers every route, the wrapper and its CSS rule go away.
 */
const V2_PAGE_PREFIXES: string[] = [
  '/store-info',
  '/sms',
  '/voicemail',
  '/billing',
  '/events',
  '/special-events',
  '/catering',
  '/knowledge-questions',
  '/getting-started',
  '/onboarding',
  // No `/hotel`: hotel pages render in `HotelShell`, never in this frame.
];

export const isV2Page = (pathname: string) =>
  V2_PAGE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

type FramePreference = 'v2' | 'legacy' | null;

const read = (value: string | null): FramePreference =>
  value === 'v2' || value === 'legacy' ? value : null;

function stored(): FramePreference {
  try {
    return read(localStorage.getItem(V2_FRAME_KEY));
  } catch {
    // Safari in private mode throws on localStorage access.
    return null;
  }
}

/** V2 by default; an explicit false/0 remains as a deployment rollback. */
export const v2FrameDefault = () =>
  process.env.REACT_APP_V2_FRAME !== 'false' &&
  process.env.REACT_APP_V2_FRAME !== '0';

export function resolveV2Frame(search: string): boolean {
  const preference = read(new URLSearchParams(search).get('frame')) ?? stored();
  return preference ? preference === 'v2' : v2FrameDefault();
}

/**
 * Reads the flag and persists an explicit `?frame=` choice, so the frame
 * survives the first navigation that drops the parameter.
 */
export function useV2Frame(): boolean {
  const { search } = useLocation();
  const override = read(new URLSearchParams(search).get('frame'));

  useEffect(() => {
    if (!override) return;
    try {
      localStorage.setItem(V2_FRAME_KEY, override);
    } catch {
      /* preference is URL-only this session */
    }
  }, [override]);

  return resolveV2Frame(search);
}
